import React, {useState, useRef, useEffect} from "react";
import {useForm} from "react-hook-form";
import {X, RefreshCw, Calendar, Clock} from "lucide-react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import FormHeader from "../../Common/FormHeader";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import {MdClose} from 'react-icons/md';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {generateVariantBarcode} from "../../../utils/barcodeGenerator";
import ProductOptionGroups from "./ProductOptionGroups";
import AppleSelect from "../../Common/AppleSelect";
import Notification from "../../Common/Notification";

const EditVarianteProductForm = () => {
    const {productId, variantId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Back to the parent product, passing on where its own "Retour" leads
    const backToProduct = () => navigate(`/catalogue/product/edit/${productId}`, {state: {returnTo: location.state?.returnTo}});

    const [currentColor, setCurrentColor] = useState("#4DB6AC");
    const [showPalette, setShowPalette] = useState(false);
    const [variantImage, setVariantImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [isStockable, setIsStockable] = useState(true);
    const [isVendable, setIsVendable] = useState(true);
    // Calendar / online booking (same settings as the product's "Paramètres calendrier")
    // salons de beauté only (busniessType === "001")
    const [isBeautySalon, setIsBeautySalon] = useState(false);
    const [showOnCalendar, setShowOnCalendar] = useState(false);
    const [duration, setDuration] = useState(30);
    // Restaurant accounts (busniessType === "004") see "Options" (dish customization);
    // all other commerce types see the same feature labeled "Packs".
    const [isRestaurant, setIsRestaurant] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [parentProduct, setParentProduct] = useState(null);
    const [units, setUnits] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [attributes, setAttributes] = useState([
        { name: "", value: "" }
    ]);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
    } = useForm();

    const formRef = useRef(null);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        // Auto hide after 5 seconds for success notifications
        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // Fetch variant data, parent product, and units
    useEffect(() => {
        const fetchData = async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const headers = {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                };
                setIsRestaurant(accountId?.busniessType === "004");
                setIsBeautySalon(accountId?.busniessType === "001");

                // Fetch units and taxes in parallel
                const [unitsRes, taxesRes] = await Promise.all([
                    api.get('/units', {headers}),
                    api.get('/taxes', {headers})
                ]);

                const unitsData = unitsRes.data;
                const taxesData = taxesRes.data;

                // Process units data
                const processedUnits = Array.isArray(unitsData) ? unitsData :
                    unitsData['hydra:member'] || unitsData.data || [];
                setUnits(processedUnits);

                // Process taxes data
                const processedTaxes = Array.isArray(taxesData) ? taxesData :
                    taxesData['hydra:member'] || taxesData.items || taxesData.data || [];
                setTaxes(processedTaxes);

                // Continue with variant fetch...
                const variantRes = await api.get(`/variants/${variantId}`, {headers});
                const variant = variantRes.data;

                // Set form values
                setValue("name", variant.name);
                setValue("description", variant.description || "");
                setValue("price", variant.price);
                setValue("barcode", variant.barcode || "");

                // Handle units
                if (variant.units) {
                    const unitId = typeof variant.units === 'string'
                        ? variant.units.split('/').pop()
                        : variant.units.id || variant.units['@id']?.split('/').pop();
                    setValue("unitsId", unitId);
                }

                // Handle tax - field is `taxe` (singular) on ProductVariant, unlike
                // Product's `taxes` (plural) — see backend ProductVariant::$taxe.
                if (variant.taxe) {
                    const taxId = typeof variant.taxe === 'string'
                        ? variant.taxe.split('/').pop()
                        : variant.taxe.id || variant.taxe['@id']?.split('/').pop();
                    setValue("taxId", taxId);
                } else if (variant.taxeId) {
                    setValue("taxId", variant.taxeId);
                }

                // Rest of your code...
                setCurrentColor(variant.color || "#4DB6AC");
                setIsActive(variant.active === true);
                setIsStockable(variant.isStockable === true);
                setIsVendable(variant.isVendable === true);
                setShowOnCalendar(variant.showOnCalendar === true);
                setDuration(variant.duration || 30);

                // Load attributes if they exist
                if (variant.attributes && Array.isArray(variant.attributes) && variant.attributes.length > 0) {
                    setAttributes(variant.attributes);
                } else if (variant.attributes && typeof variant.attributes === 'object') {
                    // Convert object format to array format
                    const attrsArray = Object.entries(variant.attributes).map(([name, value]) => ({
                        name,
                        value: String(value)
                    }));
                    setAttributes(attrsArray.length > 0 ? attrsArray : [{ name: "", value: "" }]);
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching variant data:", error);
                showNotification('error', "Erreur lors du chargement des données");
                setIsLoading(false);
            }
        };

        fetchData();
    }, [variantId, setValue]);

    const onSubmit = async (data) => {
        try {
            showNotification('loading', "Mise à jour de la déclinaison en cours...");

            // Create FormData object
            const formData = new FormData();

            // Add the ID to the form data
            formData.append("id", variantId);

            // Add necessary fields with proper values
            formData.append("name", data.name);
            formData.append("color", currentColor);
            formData.append("description", data.description || "");
            formData.append("price", data.price);
            formData.append("barcode", data.barcode || "");
            formData.append("units", `/api/units/${data.unitsId}`);
            formData.append("taxe", `/api/taxes/${data.taxId}`);  // field is `taxe` (singular) on ProductVariant
            formData.append("active", String(isActive));
            formData.append("isStockable", String(isStockable));
            formData.append("isVendable", String(isVendable));
            if (isBeautySalon) {
                formData.append("show_on_calendar", String(showOnCalendar));
                formData.append("duration", String(duration));
            }

            // Add attributes (filter out empty ones)
            const validAttributes = attributes.filter(attr => attr.name.trim() && attr.value.trim());
            if (validAttributes.length > 0) {
                formData.append("attributes", JSON.stringify(validAttributes));
            }

            // Add product reference
            if (productId) {
                formData.append("product", `/api/products/${productId}`);
            }

            // Handle image upload
            if (variantImage instanceof File) {
                formData.append("image", variantImage);
            } else if (typeof variantImage === 'string' && variantImage.startsWith('data:')) {
                const response = await fetch(variantImage);
                const blob = await response.blob();
                const file = new File([blob], "variant-image.jpg", {type: "image/jpeg"});
                formData.append("image", file);
            }

            // Get account ID
            const accountId = await secureStorage.getItem("selectedAccount");

            // Use POST + /update endpoint for updates (same mechanism as products):
            // PUT + multipart is broken server-side for file uploads, so image
            // updates must go through the dedicated /update route.
            const response = await api.post(`/variants/${variantId}/update`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            showNotification('success', "Déclinaison mise à jour avec succès");

            // Give time for notification to be seen
            setTimeout(() => {
                backToProduct();
            }, 1500);
        } catch (error) {
            console.error("Error updating variant:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur s'est produite lors de la mise à jour";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };

    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };

    const handleImageChange = (image) => {
        setVariantImage(image);
    };

    const togglePalette = () => {
        setShowPalette((prev) => !prev);
    };

    const toggleActive = () => {
        setIsActive((prev) => !prev);
    };

    const toggleStockable = () => {
        setIsStockable((prev) => !prev);
    };

    const toggleVendable = () => {
        setIsVendable((prev) => !prev);
    };

    const handleDeleteImage = () => {
        setVariantImage(null);
    };

    // Attribute management functions
    const handleAddAttribute = () => {
        setAttributes([...attributes, { name: "", value: "" }]);
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...attributes];
        newAttributes[index][field] = value;
        setAttributes(newAttributes);
    };

    const handleRemoveAttribute = (index) => {
        if (attributes.length > 1) {
            const newAttributes = attributes.filter((_, i) => i !== index);
            setAttributes(newAttributes);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary border-r-transparent align-[-0.125em]"
                        role="status">
                        <span
                            className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                    </div>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">Chargement de la déclinaison...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Responsive header */}
            <FormHeader title="Modifier la déclinaison" back={backToProduct} onSave={() => formRef.current && handleSubmit(onSubmit)()}/>

            <form
                ref={formRef}
                className="w-full px-4 sm:px-6 py-4 sm:py-8 space-y-6 sm:space-y-8"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Parent product info */}
                {parentProduct && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Produit parent
                        </h3>
                        <div className="flex items-center mt-2">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                                {parentProduct.imageUrl ? (
                                    <img
                                        src={parentProduct.thumbnailUrl || parentProduct.imageUrl}
                                        alt={parentProduct.name}
                                        className="h-8 w-8 sm:h-10 sm:w-10 rounded object-cover"
                                    />
                                ) : (
                                    <div
                                        className="h-8 w-8 sm:h-10 sm:w-10 rounded flex items-center justify-center text-white"
                                        style={{backgroundColor: parentProduct.color || '#4DB6AC'}}
                                    >
                                        {parentProduct.name.substring(0, 2).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{parentProduct.name}</div>
                                {parentProduct.barcode && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Code: {parentProduct.barcode}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Image/Color section */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                    <div className="text-center mb-1 sm:mb-2">
                        {variantImage ? (
                            <div className="relative w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 mx-auto">
                                <img
                                    src={variantImage}
                                    alt="Variant"
                                    className="w-full h-full object-cover rounded-lg shadow-sm cursor-pointer"
                                    onClick={togglePalette}
                                />
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 sm:p-1.5 shadow-sm hover:bg-red-600 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                                    aria-label="Delete image"
                                >
                                    <MdClose className="w-4 h-4"/>
                                </button>
                            </div>
                        ) : (
                            <div
                                className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-lg flex items-center justify-center cursor-pointer shadow-sm hover:shadow-md transition-shadow mx-auto text-white text-sm sm:text-base"
                                onClick={togglePalette}
                                style={{backgroundColor: currentColor}}
                            >
                                Éditer
                            </div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-2 block">
                            Cliquez pour modifier
                        </span>
                    </div>
                </div>

                {/* Form fields section */}
                <div className="space-y-4 sm:space-y-6">
                    <div>
                        <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Nom de la déclinaison :
                        </label>
                        <input
                            type="text"
                            id="name"
                            autoComplete="off"
                            {...register("name", {
                                required: "Le champ 'Nom' est obligatoire.",
                            })}
                            placeholder="Nom de la déclinaison*"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                errors.name
                                    ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                    : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                            }`}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.name.message}</p>
                        )}
                    </div>

                    <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-3 sm:space-y-4">
                            <div>
                                <label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Description (optionnelle) :
                                </label>
                                <textarea
                                    id="description"
                                    {...register("description")}
                                    rows="3"
                                    placeholder="Description de la déclinaison"
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <label htmlFor="price" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Prix :
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="price"
                                        {...register("price", {
                                            required: "Le prix est obligatoire.",
                                            min: {value: 0, message: "Le prix doit être positif"}
                                        })}
                                        placeholder="Prix*"
                                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                            errors.price
                                                ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                                : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                        }`}
                                    />
                                    {errors.price && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.price.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="barcode" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Code à barres :
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            id="barcode"
                                            {...register("barcode")}
                                            placeholder="Code à barres"
                                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 border text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const barcode = await generateVariantBarcode();
                                                    setValue("barcode", barcode, {shouldDirty: true});
                                                } catch (e) {
                                                    console.error("Erreur lors de la génération du code-barres", e);
                                                }
                                            }}
                                            title="Générer un code-barres"
                                            className="flex items-center gap-1.5 px-3 py-2 sm:py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                                        >
                                            <RefreshCw size={16}/>
                                            <span className="hidden sm:inline">Générer</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="unitsId" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Unité :
                                </label>
                                <AppleSelect
                                    id="unitsId"
                                    {...register("unitsId", {
                                        required: "L'unité est obligatoire."
                                    })}
                                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                        errors.unitsId
                                            ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                            : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                    }`}
                                >
                                    <option value="">Sélectionner une unité</option>
                                    {units && units.length > 0 ? (
                                        units.map(unit => (
                                            <option key={unit.id} value={unit.id}>
                                                {unit.name} {unit.code ? `(${unit.code})` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>Chargement des unités...</option>
                                    )}
                                </AppleSelect>
                                {errors.unitsId && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.unitsId.message}</p>
                                )}
                            </div>

                            {/* Tax dropdown */}
                            <div className="mt-3 sm:mt-4">
                                <label htmlFor="taxId" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Taux de TVA :
                                </label>
                                <AppleSelect
                                    id="taxId"
                                    {...register("taxId", {
                                        required: "Le taux de TVA est obligatoire."
                                    })}
                                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                        errors.taxId
                                            ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                            : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                    }`}
                                >
                                    <option value="">Sélectionner un taux de TVA</option>
                                    {taxes && taxes.length > 0 ? (
                                        taxes.map(tax => (
                                            <option key={tax.id} value={tax.id}>
                                                {tax.value}% {tax.name ? `(${tax.name})` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>Chargement des taux de TVA...</option>
                                    )}
                                </AppleSelect>
                                {errors.taxId && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.taxId.message}</p>
                                )}
                            </div>

                            {/* Attributes Section */}
                            <div className="mt-4 sm:mt-6">
                                <div className="flex justify-between items-center mb-3">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Attributs de déclinaison :
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAddAttribute}
                                        className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                                    >
                                        + Ajouter attribut
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {attributes.map((attribute, index) => (
                                        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                            <div>
                                                <input
                                                    type="text"
                                                    value={attribute.name}
                                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                                    placeholder="Nom (ex: Taille, Couleur)"
                                                    className="w-full px-3 py-2 border text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={attribute.value}
                                                    onChange={(e) => handleAttributeChange(index, "value", e.target.value)}
                                                    placeholder="Valeur (ex: M, Rouge)"
                                                    className="flex-1 px-3 py-2 border text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                />
                                                {attributes.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAttribute(index)}
                                                        className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    Les attributs permettent de différencier cette déclinaison (ex: Taille: M, Couleur: Rouge)
                                </p>
                            </div>

                            {/* "Options" (Restaurant) / "Packs" (other commerce types) */}
                            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">
                                    {isRestaurant ? "Options" : "Packs"} :
                                </label>
                                <ProductOptionGroups
                                    variantId={variantId}
                                    groupLabel={isRestaurant ? "groupe" : "pack"}
                                    onNotify={showNotification}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4">
                            <div className="flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Actif</span>
                                <IOSSwitch
                                    checked={isActive}
                                    onChange={toggleActive}
                                />
                            </div>

                            <div className="flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stockable</span>
                                <IOSSwitch
                                    checked={isStockable}
                                    onChange={toggleStockable}
                                />
                            </div>

                            <div className="flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendable</span>
                                <IOSSwitch
                                    checked={isVendable}
                                    onChange={toggleVendable}
                                />
                            </div>
                        </div>

                        {/* Calendrier : visibilité sur le calendrier / la réservation en ligne + durée (salon uniquement) */}
                        {isBeautySalon && (
                        <div className="mt-3 sm:mt-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                                <span className="flex items-start gap-2">
                                    <Calendar className="w-4 h-4 mt-0.5 text-primary flex-shrink-0"/>
                                    <span>
                                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Afficher dans le calendrier</span>
                                        <span className="block text-xs text-gray-400 mt-0.5">Proposée au calendrier et à la réservation en ligne</span>
                                    </span>
                                </span>
                                <IOSSwitch
                                    checked={showOnCalendar}
                                    onChange={() => setShowOnCalendar(prev => !prev)}
                                />
                            </div>

                            {showOnCalendar && (
                                <div className="flex items-center gap-2 pl-3 sm:ml-6 border-l-2 border-primary/30">
                                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                    <label htmlFor="variant-duration" className="text-sm text-gray-700 dark:text-gray-300">Durée</label>
                                    <input
                                        id="variant-duration"
                                        type="number"
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                                        min="5"
                                        step="5"
                                        className="w-20 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    />
                                    <span className="text-sm text-gray-400">minutes</span>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                </div>
            </form>

            {/* Color palette overlay - responsive & scrollable */}
            {showPalette && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                             onClick={e => e.stopPropagation()}>
                            <ColorPalette
                                initialColor={currentColor}
                                onClose={togglePalette}
                                onColorChange={handleColorChange}
                                onImageChange={handleImageChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default EditVarianteProductForm;
